/* ============================================================
   Shared resend/"I've confirmed" logic for the email-verification wall
   (screens/VerifyEmailWall.jsx) AND the Settings nudge card
   (screens/Settings.jsx ProfileTab) — extracted 2026-09-10 when
   emailVerificationWallEnabled() (flags.js) went default OFF: most users now
   never see the wall, so Settings carries the same resend/confirm actions.
   Both call sites share the exact same request + state machine; only the
   surrounding copy/markup differs per screen. VerifyEmailWall's own rendered
   text is unchanged by this extraction — it still owns its own t() calls.

   Split into pure/framework-free pieces so the request logic is unit-testable
   without a DOM (this repo has no @testing-library/jsdom — see
   hooks/useDismissableMenu.js for the established convention).
   ============================================================ */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext.jsx';
import { api } from '../services/api/client.js';

// Pure: whether a resend click should actually fire the request. The button
// is already `disabled={sending}` in both UIs; this additionally guards a
// stray call (e.g. a queued Enter keypress) while one is still in flight.
export function shouldResend({ sending }) {
  return !sending;
}

// Pure: run the resend request against an injectable `resendVerification`
// call so it's testable without React/DOM. Mirrors VerifyEmailWall's
// original inline try/catch exactly: success -> { ok: true }; failure ->
// { ok: false, message } using the server error's message, falling back to
// `fallbackMessage` when the error carries none.
export async function runResend({ resendVerification, fallbackMessage }) {
  try {
    await resendVerification();
    return { ok: true };
  } catch (err) {
    return { ok: false, message: err?.message || fallbackMessage };
  }
}

export function useEmailVerificationActions() {
  const { t } = useTranslation();
  const { reloadUser } = useAuth();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);

  async function resend() {
    if (!shouldResend({ sending })) return;
    setError(null);
    setSending(true);
    const result = await runResend({
      resendVerification: () => api.auth.resendVerification(),
      fallbackMessage: t('auth.couldNotResend', 'Could not resend the link. Please try again.'),
    });
    if (result.ok) setSent(true);
    else setError(result.message);
    setSending(false);
  }

  // Re-fetch /me. If the user has confirmed in another tab, emailVerified
  // flips true and the caller hides/unmounts on its own (VerifyEmailWall's
  // conditional render in App.jsx, or the Settings card's
  // `emailVerified === false` guard).
  async function refresh() {
    setChecking(true);
    try {
      // Explicit confirmation can follow a verification in another tab. Do
      // not reuse cached identity or the pre-verification billing decision.
      api.invalidateCache(['/api/auth/me', '/api/billing/entitlements']);
      await reloadUser();
    } finally {
      setChecking(false);
    }
  }

  return { sending, sent, checking, error, resend, refresh };
}
